import { useCallback, useRef, useState } from 'react';
import { yieldToMain } from '../utils/yieldToMain';

const baseState = {
  loading: false,
  error: null,
  stage: '',
  progress: 0,
  result: null,
};

export const useCancelableTask = () => {
  const [state, setState] = useState(baseState);
  const runIdRef = useRef(0);

  const setError = useCallback((error) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setResult = useCallback((result) => {
    setState((prev) => ({ ...prev, result }));
  }, []);

  const setStage = useCallback((stage, progress) => {
    setState((prev) => ({
      ...prev,
      stage,
      progress: Number.isFinite(progress) ? progress : prev.progress,
    }));
  }, []);

  const setProgress = useCallback((progress) => {
    setState((prev) => ({
      ...prev,
      progress: Number.isFinite(progress) ? progress : prev.progress,
    }));
  }, []);

  const cancel = useCallback(() => {
    runIdRef.current += 1;
    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  const reset = useCallback(() => {
    setState(baseState);
  }, []);

  const run = useCallback(async (taskFn, options = {}) => {
    const { preserveResult = false } = options;
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;

    setState((prev) => ({
      ...baseState,
      loading: true,
      result: preserveResult ? prev.result : null,
    }));

    const isActive = () => runIdRef.current === runId;
    const updateResult = (result) => {
      if (!isActive()) return;
      setState((prev) => ({ ...prev, result }));
    };
    const updateError = (error) => {
      if (!isActive()) return;
      setState((prev) => ({ ...prev, error }));
    };
    const updateStage = (stage, progress) => {
      if (!isActive()) return;
      setState((prev) => ({
        ...prev,
        stage,
        progress: Number.isFinite(progress) ? progress : prev.progress,
      }));
    };
    const updateProgress = (progress) => {
      if (!isActive()) return;
      setState((prev) => ({
        ...prev,
        progress: Number.isFinite(progress) ? progress : prev.progress,
      }));
    };

    try {
      await taskFn({
        isActive,
        setResult: updateResult,
        setError: updateError,
        updateStage,
        updateProgress,
        yieldToMain,
      });
    } finally {
      if (isActive()) {
        setState((prev) => ({ ...prev, loading: false }));
      }
    }
  }, []);

  return {
    ...state,
    run,
    cancel,
    reset,
    setError,
    setResult,
    setStage,
    setProgress,
  };
};
